import { useState, useEffect } from "react";
import { Form } from "react-admin";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import useDrivePicker from "react-google-drive-picker";
import LoadingOverlay from "../../../core/LoadingOverlay";
import ConfirmDialog from "../../../core/ConfirmDialog";
import DismissibleMessage from "../DismissibleMessage";
import { config } from "../../../../config";
import { calibrationService } from "../../../../services/CalibrationService";
import { ClientsDropdown } from "../DropdownV2";
import { useStyles } from "./styles";
import { GOOGLE_DRIVE_SCOPE } from "./constants";

export default function UploadTab() {
    const classes = useStyles();
    const [openPicker, data] = useDrivePicker();
    const [clientName, setClientName] = useState("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    // Selected Google Drive files, each { id, name }.
    const [files, setFiles] = useState([]);
    const [accessToken, setAccessToken] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    useEffect(() => {
        if (data) {
            setAccessToken(data.access_token);
        }
    }, [data]);

    const handleOpenPicker = () => {
        openPicker({
            clientId: config.googleClientId,
            developerKey: config.googleApiDeveloperKey,
            token: data?.access_token,
            viewId: "DOCS",
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: true,
            customScopes: [GOOGLE_DRIVE_SCOPE],
            callbackFunction: (pickerAction) => {
                if (pickerAction.action === "picked") {
                    setFiles(pickerAction.docs.map((doc) => ({ id: doc.id, name: doc.name })));
                }
            },
        });
    };

    const handleSubmit = async () => {
        setConfirmModalOpen(false);
        setSubmitting(true);
        setUploadProgress({ done: 0, total: files.length });

        // Upload each file independently so one failure doesn't abort the rest.
        // Runs sequentially to keep the progress counter meaningful and avoid
        // hammering the backend (each call downloads from Google Drive server-side).
        const failures = [];
        let done = 0;
        for (const file of files) {
            try {
                await calibrationService.plscaddKmzIngress(clientName, file.id, accessToken);
            } catch (err) {
                failures.push(`${file.name} (${err})`);
            }
            done += 1;
            setUploadProgress({ done, total: files.length });
        }

        const succeeded = files.length - failures.length;
        if (failures.length === 0) {
            setStatusMessage(
                `Uploaded ${succeeded} PLS-CADD KMZ${succeeded === 1 ? "" : "s"} for client "${clientName}".`
            );
            setFiles([]);
        } else {
            setErrorMessage(
                `${succeeded}/${files.length} uploaded. Failed: ${failures.join("; ")}`
            );
            // Keep the failed files selected so the user can retry them.
            setFiles(files.filter((file) => failures.some((f) => f.startsWith(`${file.name} (`))));
        }
        setUploadProgress(null);
        setSubmitting(false);
    };

    return (
        <div style={{ paddingBottom: "100px" }}>
            <Typography
                variant="body2"
                style={{ textAlign: "center", maxWidth: "600px", margin: "0 auto 20px", color: "#666" }}
            >
                Upload one or more PLS-CADD formatted .kmz files from Google Drive. Each is stored as a
                <strong> plscadd-kmz</strong> dataset.
            </Typography>
            <ConfirmDialog
                open={confirmModalOpen}
                title="Confirm PLS-CADD KMZ Upload"
                handleClose={() => setConfirmModalOpen(false)}
                handleConfirm={handleSubmit}
                message={`Upload ${files.length} KMZ${files.length === 1 ? "" : "s"} for client "${clientName}"?`}
                newLineMessage={files.map((file) => file.name).join(", ")}
            />
            <Form className={classes.formContainer}>
                <DismissibleMessage message={errorMessage} type="error" onDismiss={() => setErrorMessage(null)} />
                <DismissibleMessage message={statusMessage} type="success" onDismiss={() => setStatusMessage(null)} />
                <LoadingOverlay
                    isLoading={submitting}
                    message={
                        uploadProgress
                            ? `Uploading PLS-CADD KMZs (${uploadProgress.done}/${uploadProgress.total})...`
                            : "Uploading PLS-CADD KMZs..."
                    }
                />
                <div className={classes.inputRow}>
                    <ClientsDropdown
                        value={clientName}
                        setValue={setClientName}
                        setError={setErrorMessage}
                        freeSolo={true}
                        styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                    />
                </div>
                <div className={classes.buttonRow}>
                    <Button variant="contained" onClick={handleOpenPicker}>
                        Select .kmz File(s) From Google Drive
                    </Button>
                </div>
                {files.length > 0 && (
                    <div className={classes.fileInfo}>
                        <Typography variant="body1" style={{ marginBottom: "6px" }}>
                            Selected {files.length} file{files.length === 1 ? "" : "s"}:
                        </Typography>
                        <List dense sx={{ maxWidth: "400px" }}>
                            {files.map((file) => (
                                <ListItem
                                    key={file.id}
                                    disableGutters
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            size="small"
                                            aria-label={`remove ${file.name}`}
                                            onClick={() => setFiles(files.filter((f) => f.id !== file.id))}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    }
                                >
                                    <ListItemText primary={file.name} />
                                </ListItem>
                            ))}
                        </List>
                    </div>
                )}
                <div className={classes.buttonRow}>
                    <Button
                        variant="contained"
                        disabled={files.length === 0 || !clientName || submitting}
                        onClick={() => setConfirmModalOpen(true)}
                    >
                        Submit
                    </Button>
                </div>
            </Form>
        </div>
    );
}
